import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Package,
  ArrowClockwise,
  PencilSimple,
} from "@phosphor-icons/react";
import {
  Link,
  useParams,
} from "react-router-dom";
import { toast } from "sonner";

import { gmao } from "@/api";
import PartFormModal from "@/components/gmao/PartFormModal";
import StockMovementModal from "@/components/gmao/StockMovementModal";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";


const STATUS_LABELS = {
  available: "Disponible",
  low_stock: "Stock faible",
  out_of_stock: "Rupture",
};


const STATUS_CLASSES = {
  available:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  low_stock:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  out_of_stock:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
};


const MOVEMENT_LABELS = {
  opening_balance: "Stock initial",
  receipt: "Réception",
  adjustment: "Correction d’inventaire",
};


function formatMoney(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "Non renseigné";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
    }
  ).format(value);
}


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
}


export default function GmaoPartDetail() {
  const { partId } =
    useParams();

  const { user } = useAuth();

  const canManage = [
    "admin",
    "manager",
    "maintenance_manager",
  ].includes(user?.role);

  const [part, setPart] =
    useState(null);

  const [movements, setMovements] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [editor, setEditor] =
    useState(false);

  const [
    movementEditor,
    setMovementEditor,
  ] = useState(null);


  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [
        partData,
        movementData,
      ] = await Promise.all([
        gmao.getPart(partId),
        canManage
          ? gmao.listPartMovements(
              partId,
              {
                limit: 1000,
              }
            )
          : Promise.resolve([]),
      ]);

      setPart(partData);

      setMovements(
        Array.isArray(movementData)
          ? movementData
          : []
      );
    } catch (requestError) {
      const message =
        formatApiError(
          requestError.response
            ?.data?.detail
        );

      setError(message);

      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [
    canManage,
    partId,
  ]);


  useEffect(() => {
    load();
  }, [load]);


  const savePart = async (
    payload
  ) => {
    setSaving(true);

    try {
      await gmao.updatePart(
        part.id,
        payload
      );

      toast.success(
        "Pièce modifiée"
      );

      setEditor(false);

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response
            ?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const saveMovement = async (
    payload
  ) => {
    setSaving(true);

    try {
      if (
        movementEditor ===
        "receive"
      ) {
        await gmao.receivePart(
          part.id,
          payload
        );

        toast.success(
          "Réception enregistrée"
        );
      } else {
        await gmao.adjustPart(
          part.id,
          payload
        );

        toast.success(
          "Stock corrigé"
        );
      }

      setMovementEditor(null);

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response
            ?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  const toggleArchive = async () => {
    if (
      !window.confirm(
        part.isArchived
          ? "Restaurer cette pièce ?"
          : "Archiver cette pièce ? Le stock doit être à zéro."
      )
    ) {
      return;
    }

    setSaving(true);

    try {
      if (part.isArchived) {
        await gmao.restorePart(
          part.id
        );

        toast.success(
          "Pièce restaurée"
        );
      } else {
        await gmao.archivePart(
          part.id
        );

        toast.success(
          "Pièce archivée"
        );
      }

      await load();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response
            ?.data?.detail
        )
      );
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="label-eyebrow lirah-pulse">
          Chargement de la pièce
        </div>
      </div>
    );
  }


  if (
    error ||
    !part
  ) {
    return (
      <div className="max-w-3xl p-4 sm:p-6 lg:p-8">
        <Link
          to="/app/gmao/parts"
          className="text-sm font-heading font-semibold text-[var(--do-accent)]"
        >
          Retour au stock
        </Link>

        <div className="mt-5 lirah-card p-8">
          <h1 className="font-heading text-xl font-black">
            Pièce indisponible
          </h1>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            {error}
          </p>
        </div>
      </div>
    );
  }


  return (
    <div
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="gmao-part-detail-page"
    >
      <Link
        to="/app/gmao/parts"
        data-testid="part-detail-back"
        className="text-sm font-heading font-semibold text-[var(--do-accent)]"
      >
        Retour au stock
      </Link>

      <div className="mt-4 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="label-eyebrow">
            {part.reference}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-2xl font-black tracking-tight sm:text-3xl">
              {part.name}
            </h1>

            <span
              className={`rounded-full border px-2.5 py-1 text-xs font-heading font-semibold ${
                STATUS_CLASSES[
                  part.stockStatus
                ] || ""
              }`}
            >
              {STATUS_LABELS[
                part.stockStatus
              ] ||
                part.stockStatus}
            </span>

            {part.isArchived && (
              <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-xs font-heading font-semibold text-[var(--do-accent)]">
                Archivée
              </span>
            )}
          </div>

          <p className="mt-2 text-sm text-[var(--do-muted)]">
            {part.description ||
              "Aucune description."}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={load}
            disabled={saving}
            data-testid="refresh-part-detail"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold"
          >
            <ArrowClockwise
              size={16}
            />

            Actualiser
          </button>

          {canManage &&
            !part.isArchived && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setMovementEditor(
                      "receive"
                    )
                  }
                  disabled={saving}
                  data-testid="part-detail-receive"
                  className="rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold"
                >
                  Réception
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setMovementEditor(
                      "adjust"
                    )
                  }
                  disabled={saving}
                  data-testid="part-detail-adjust"
                  className="rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold"
                >
                  Corriger
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setEditor(true)
                  }
                  disabled={saving}
                  data-testid="part-detail-edit"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold"
                >
                  <PencilSimple
                    size={16}
                  />

                  Modifier
                </button>
              </>
            )}

          {canManage && (
            <button
              type="button"
              onClick={
                toggleArchive
              }
              disabled={saving}
              data-testid="part-detail-archive"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold"
            >
              <Package
                size={16}
              />

              {part.isArchived
                ? "Restaurer"
                : "Archiver"}
            </button>
          )}
        </div>
      </div>

      <div className={`mt-6 grid grid-cols-2 gap-3 ${
        canManage
          ? "lg:grid-cols-4"
          : "lg:grid-cols-3"
      }`}>
        <div className="lirah-card p-4">
          <div className="label-eyebrow">
            Stock disponible
          </div>

          <div className="mt-1 font-heading text-2xl font-black">
            {part.quantityOnHand}
          </div>
        </div>

        <div className="lirah-card p-4">
          <div className="label-eyebrow">
            Seuil minimum
          </div>

          <div className="mt-1 font-heading text-2xl font-black">
            {part.minimumQuantity}
          </div>
        </div>

        <div className="lirah-card p-4">
          <div className="label-eyebrow">
            Emplacement
          </div>

          <div className="mt-1 font-heading font-black">
            {part.location ||
              "Non renseigné"}
          </div>
        </div>

        {canManage && (
          <div className="lirah-card p-4">
            <div className="label-eyebrow">
              Valeur du stock HT
            </div>

            <div className="mt-1 font-heading text-xl font-black">
              {formatMoney(
                part.stockValueHt
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="lirah-card p-5">
          <div className="label-eyebrow">
            Catalogue
          </div>

          <h2 className="mt-1 font-heading text-lg font-black">
            Informations
          </h2>

          <dl className="mt-4 grid gap-4 text-sm">
            <div>
              <dt className="text-[var(--do-muted)]">
                Catégorie
              </dt>

              <dd className="mt-1 font-semibold">
                {part.category ||
                  "Non renseignée"}
              </dd>
            </div>

            <div>
              <dt className="text-[var(--do-muted)]">
                Fournisseur
              </dt>

              <dd className="mt-1 font-semibold">
                {part.supplier ||
                  "Non renseigné"}
              </dd>
            </div>

            <div>
              <dt className="text-[var(--do-muted)]">
                Unité
              </dt>

              <dd className="mt-1 font-semibold">
                {part.unit}
              </dd>
            </div>

            {canManage && (
              <div>
                <dt className="text-[var(--do-muted)]">
                  Prix d’achat unitaire HT
                </dt>

                <dd className="mt-1 font-semibold">
                  {formatMoney(
                    part.unitCostHt
                  )}
                </dd>
              </div>
            )}
          </dl>
        </section>

        <section className="lirah-card p-5">
          <div className="label-eyebrow">
            Traçabilité
          </div>

          <h2 className="mt-1 font-heading text-lg font-black">
            Cycle de vie
          </h2>

          <dl className="mt-4 grid gap-4 text-sm">
            <div>
              <dt className="text-[var(--do-muted)]">
                Créée le
              </dt>

              <dd className="mt-1 font-semibold">
                {formatDate(
                  part.createdAt
                )}
              </dd>
            </div>

            <div>
              <dt className="text-[var(--do-muted)]">
                Dernière modification
              </dt>

              <dd className="mt-1 font-semibold">
                {formatDate(
                  part.updatedAt
                )}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      {canManage && (
        <section
          className="mt-6 lirah-card p-5"
          data-testid="part-movements"
        >
          <div className="label-eyebrow">
            Stock
          </div>

          <h2 className="mt-1 font-heading text-xl font-black">
            Historique des mouvements
          </h2>

          {movements.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--do-muted)]">
              Aucun mouvement enregistré.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {movements.map(
                (movement) => (
                  <article
                    key={movement.id}
                    className="rounded-xl border border-[var(--do-border)] p-4"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-heading font-bold">
                          {MOVEMENT_LABELS[
                            movement.movementType
                          ] ||
                            movement.movementType}
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {formatDate(
                            movement.createdAt
                          )}
                        </div>
                      </div>

                      <div className={`font-heading text-lg font-black ${
                        movement.quantityDelta >
                        0
                          ? "text-[var(--do-accent)]"
                          : "text-[var(--do-accent)]"
                      }`}>
                        {movement.quantityDelta >
                        0
                          ? "+"
                          : ""}
                        {movement.quantityDelta}
                      </div>
                    </div>

                    <div className="mt-3 text-sm">
                      {movement.quantityBefore}
                      {" → "}
                      <strong>
                        {movement.quantityAfter}
                      </strong>
                    </div>

                    {movement.note && (
                      <p className="mt-2 text-sm text-[var(--do-muted)]">
                        {movement.note}
                      </p>
                    )}
                  </article>
                )
              )}
            </div>
          )}
        </section>
      )}

      <PartFormModal
        open={editor}
        mode="edit"
        part={part}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setEditor(false);
          }
        }}
        onSubmit={savePart}
      />

      <StockMovementModal
        open={Boolean(
          movementEditor
        )}
        mode={
          movementEditor ||
          "receive"
        }
        part={part}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setMovementEditor(
              null
            );
          }
        }}
        onSubmit={
          saveMovement
        }
      />
    </div>
  );
}
